import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
// eslint-disable-next-line no-restricted-imports
import { UntypedFormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { debounceTime, finalize, switchMap } from 'rxjs/operators';

import {
  ApBaseComponent,
  ApFileUploadService,
  ApNotificationService,
  ApUndoableOperationService,
  DocumentService,
  mapToModalSuccessValue,
  reportErrorToUserAndServer,
} from 'webcommon/core-ui';
import { BrowserService, FindControlIdService } from 'webcommon/legacy-common';
import {
  ActionIconDto,
  ColumnActionIconsDto,
  ColumnCheckboxDto,
  ColumnInfoDto,
  ColumnItemDto,
  ColumnRadioButtonDto,
  FindControlAdditionalSearchParametersDto,
  FindControlInitDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  FindControlSearchDto,
  HiddenActionItemType,
  IColumnPk,
  IDeleted,
  ISelected,
} from 'webcommon/shared';

import { ApFindControlService } from '../ap-findcontrol.service';
import { FcFilterModalService } from '../fc-filter-modal.service';
import { ApFindControlImportModalService } from '../import-modal/index';
import { ApFindControlReorderColumnsModalService } from '../reorder-columns-modal/index';
import { FindControlMode } from '../../models/index';
import { CheckboxData, FcAdvancedConfig } from './fc-advanced-config';
import { FindControlCustomData } from '../FindControlCustomData';
import { ApConfigurableTableService } from '../../ap-configurable-table/ap-configurable-table.service';

export enum ApFindControlAdvancedUpdateAction {
  Hide,
  Modify,
  New,
  Show,
}

export interface ApFindControlAdvancedUpdate {
  action: ApFindControlAdvancedUpdateAction;
  dataItem: IColumnPk & ISelected & IDeleted;
}

@Component({
  selector: 'ap-findcontrol-advanced',
  templateUrl: './ap-findcontrol-advanced.component.html',
  styleUrls: ['./ap-findcontrol-advanced.component.less'],
})
export class ApFindControlAdvancedComponent extends ApBaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() findControlId: string;
  @Input() baseId: string;
  @Input() requiredSearchParameters?: FindControlRequiredSearchParametersDto;
  @Input() additionalSearchParameters: FindControlAdditionalSearchParametersDto | null = null;
  @Input() mode: FindControlMode;
  @Input() updateObs: Observable<ApFindControlAdvancedUpdate> | undefined;
  @Input() undoComponentId: number;
  @Input() hiddenActionItems?: HiddenActionItemType[];
  @Input() customData?: FindControlCustomData;

  @Output() deleteClick = new EventEmitter<IColumnPk & IDeleted>();
  @Output() finishLoad = new EventEmitter<FindControlInitDto>();
  @Output() finishLoadData = new EventEmitter<FindControlResultDto>();
  @Output() hyperlinkClick = new EventEmitter<IColumnPk & ISelected>();
  @Output() modifyClick = new EventEmitter<IColumnPk & ISelected>();
  @Output() newClick = new EventEmitter();
  @Output() selectChange = new EventEmitter<IColumnPk & ISelected>();

  public catchAdditionalSearchParameters: FindControlAdditionalSearchParametersDto | null = null;
  public clientColumnInfoList: Array<ColumnInfoDto<string>>;
  public dataList: Array<IColumnPk & ISelected> = [];
  public findControlDto: FindControlInitDto;
  public formControl = new UntypedFormControl();
  public checkboxData: CheckboxData[] | null = [];
  public checkboxFormGroup = new FormGroup({});
  public loadingConfig = true;
  public loadingData = false;
  public serverColumnItemList: Array<ColumnItemDto<string>>;
  public maxRows: number;
  public displayFilterOption: string;
  public displayRowCount: string;
  public removeFiltersOption = true;
  public rowCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  public isModifyButtonHidden = false;
  public isDeleteButtonHidden = false;
  public isNewButtonHidden = false;
  public subscription: Subscription;
  public isShowInactive: boolean | null = false;
  public userSearchParameters?: FindControlAdditionalSearchParametersDto;

  private radioButtonColumnFieldConfig: ColumnRadioButtonDto<IColumnPk & ISelected> | undefined;
  private readonly prepopulateValue: Record<number, string> = {
    0: 'None',
    1: 'All',
    2: 'Most Frequently Used',
    3: 'Most Recently Used',
    4: 'Most Frequently Used',
  };

  private readonly waitTime: number = 300;

  constructor(
    public apNotificationService: ApNotificationService,
    private apConfigurableTableService: ApConfigurableTableService,
    private apFindControlService: ApFindControlService,
    private readonly apFindControlImportModalService: ApFindControlImportModalService,
    private readonly apFindControlReorderColumnsModalService: ApFindControlReorderColumnsModalService,
    private apUndoableOperationService: ApUndoableOperationService,
    private readonly apFileUploadService: ApFileUploadService,
    private readonly browserService: BrowserService,
    private readonly documentService: DocumentService,
    public findControlIdService: FindControlIdService,
    public fcAdvancedConfig: FcAdvancedConfig,
    private readonly fcFilterModalService: FcFilterModalService,
  ) {
    super();
  }

  ngOnInit() {
    this.loadConfig();
    if (this.hiddenActionItems) {
      this.isDeleteButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.Delete);
      this.isModifyButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.Modify);
      this.isNewButtonHidden = this.hiddenActionItems.includes(HiddenActionItemType.New);
    }

    this.formControl.valueChanges.pipe(debounceTime(this.waitTime), this.takeUntilDestroy()).subscribe((searchString) => {
      if (!searchString) return;
      this.loadData(searchString);
    });

    this.catchAdditionalSearchParameters = this.additionalSearchParameters;
  }

  public removeFilters(): void {
    this.loadData('%');
    this.removeFiltersOption = false;
    this.displayCountLineItem();
  }

  public displayCountLineItem(): void {
    this.rowCount$.pipe(this.takeUntilDestroy()).subscribe((countLineItem) => {
      if (countLineItem === this.maxRows) {
        this.displayRowCount = `maximum ${countLineItem} results`;
      } else {
        this.displayRowCount = `${countLineItem} results`;
      }
    });
  }

  private getCheckboxData(): void {
    this.checkboxData = [];
    this.checkboxFormGroup = new FormGroup({});
    const getCheckbox = this.fcAdvancedConfig.fcAdvancedConfig.get(this.findControlId);
    switch (true) {
      case (getCheckbox === undefined):
        this.checkboxData.push(this.fcAdvancedConfig.defaultInactiveCheckbox);
        this.checkboxFormGroup.addControl(this.checkboxData[0].formControlName, this.checkboxData[0].formControl);
        break;
      case (this.getIsCheckboxTrue(getCheckbox)):
        if (getCheckbox) {
          this.checkboxData = getCheckbox;
          this.checkboxData?.forEach((formControl: CheckboxData) => {
            this.checkboxFormGroup.addControl(formControl.formControlName, formControl.formControl);
          });
        }
        break;
      default:
        return;
    }

    if (this.checkboxData && this.checkboxData.length > 0) {
      this.checkboxData?.forEach((element: CheckboxData) => {
        element?.formControl.setValue(element?.isFormControlDefaultValue, { emitEvent: false });
      });
    }
  }

  private getIsCheckboxTrue(getCheckbox: CheckboxData[] | null | undefined): boolean {
    return !!getCheckbox && getCheckbox.length > 1;
  }

  public onCheckboxChange(checkbox: CheckboxData): void {
    const defaultCheckboxName = this.fcAdvancedConfig.defaultInactiveCheckbox.formControlName;
    if (checkbox.formControlName === defaultCheckboxName) {
      this.isShowInactive = checkbox?.formControl?.value;
    } else {
      this.getAdditionalSearchParameters(checkbox);
    }
    this.loadData(this.formControl.value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.loadingConfig && changes.findControlId) {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      this.loadConfig();
    }

    if (changes.findControlId.previousValue !== changes.findControlId.currentValue) {
      this.getCheckboxData();
      if (this.checkboxData && this.checkboxData.length > 1) {
        this.additionalSearchParameters = this.catchAdditionalSearchParameters;
      }
      this.isShowInactive = false;
      this.formControl.setValue('');
      this.loadData(this.formControl.value);
    }
  }

  private updateRowCount(): void {
    if (!this.removeFiltersOption) {
      this.rowCount$.next(this.dataList.filter((item) => !item.Deleted).length);
    }
  }

  private loadConfig() {
    this.loadingConfig = true;
    this.userSearchParameters = undefined;
    const configObs = this.customData && this.findControlId === this.findControlIdService.CustomData
      ? of(this.customData.Config)
      : this.apFindControlService.getConfig(this.findControlId);

    configObs.pipe(
      finalize(() => this.onLoadConfigComplete()),
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => this.onLoadConfigSuccess(data, true),
      () => this.onError(),
    );

    if (this.updateObs) {
      this.subscription = this.updateObs.pipe(this.takeUntilDestroy()).subscribe((tableUpdate) => {
        switch (tableUpdate.action) {
          case ApFindControlAdvancedUpdateAction.Hide:
            this.hideItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.Modify:
            this.updateItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.New:
            this.appendItem(tableUpdate.dataItem);
            break;
          case ApFindControlAdvancedUpdateAction.Show:
            this.showItem(tableUpdate.dataItem);
            break;
        }
      });
    }
  }

  private appendItem(dataItem: IColumnPk): void {
    const newDataList = [...this.dataList];
    newDataList.push(dataItem);
    this.dataList = newDataList;
    this.updateRowCount();
  }

  private hideItem(dataItem: IDeleted): void {
    dataItem.Deleted = true;
    this.updateRowCount();
  }

  private showItem(dataItem: IDeleted): void {
    dataItem.Deleted = false;
    this.updateRowCount();
  }

  private updateItem(dataItem: IColumnPk) {
    const existingItemIndex = this.dataList.findIndex((x) => x.Pk === dataItem.Pk);
    if (existingItemIndex !== -1) {
      const newDataList = [...this.dataList];
      newDataList.splice(existingItemIndex, 1, dataItem);
      this.dataList = newDataList;
    }
  }

  private loadData(searchStr: string, firstLoad = false) {
    const requiredSearchParams = this.requiredSearchParameters || {
      RequiredSearchParameters: [],
    };
    if(this.userSearchParameters && this.findControlId === 'ViewFindPatient'){
      for(const column of this.userSearchParameters.AdditionalSearchParameters){
        if(column.ColumnName === 'Name'){
          column.ColumnName = 'FullName';
        }
      }
    }
    const searchDto: FindControlSearchDto = {
      AdditionalSearchParameters: this.additionalSearchParameters,
      Prepopulate: firstLoad,
      RequiredSearchParameters: requiredSearchParams,
      SearchText: searchStr,
      ShowInactive: this.isShowInactive ? this.isShowInactive : false,
    };

    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      this.loadingData = true;
      this.customData = this.getCustomData();
      const getDataObs = this.customData && this.findControlId === this.findControlIdService.CustomData
        ? of(this.apFindControlService.getCustomData(searchDto, this.customData))
        : this.apFindControlService.getData(this.findControlId, searchDto, this.userSearchParameters);
      const promise = getDataObs.toPromise().then((data) => {
        this.onLoadDataSuccess(data);
        if (firstLoad) {
          this.removeFiltersOption = true;
        } else {
          this.removeFiltersOption = false;
          this.updateRowCount();
          this.displayCountLineItem();
        }
      }).finally(() => this.onLoadDataComplete());
      return {
        completionPromise: promise,
      };
    });
  }

  onExportAllClick() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const filename = this.documentService.sanitizeImportExportFilename(
        `${this.findControlDto.FindControlBehavior.Name}_AllItems.xml`,
      );
      const promise = this.apFindControlService.exportAll(this.findControlDto.FindControlBehavior).pipe(
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy(),
      ).toPromise().then(result => {
        if (!result) {
          return;
        }

        const blob = new Blob([ result.Xml ], { type: 'application/xml' });
        this.browserService.saveBlob(
          this.browserService.getDocument(),
          blob,
          filename,
        );
      });

      return {
        completionPromise: promise,
      };
    });
  }

  getSelectedItemIds() {
    let itemIds: string[];
    if (this.radioButtonColumnFieldConfig) {
      if (!this.radioButtonColumnFieldConfig.SelectedRowPk) {
        itemIds = [];
      } else {
        itemIds = [ this.radioButtonColumnFieldConfig.SelectedRowPk ];
      }
    } else {
      itemIds = this.dataList.filter(x => x.Selected).map(x => x.Pk);
    }
    return itemIds;
  }

  onExportClick() {
    const itemIds = this.getSelectedItemIds();
    this.exportItems(itemIds);
  }

  onExportCurrentClick() {
    const itemIds = this.dataList.map(x => x.Pk);
    this.exportItems(itemIds);
  }

  private exportItems(itemIds: string[]) {
    const findControlBehavior = this.findControlDto.FindControlBehavior;
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const promise = this.apFindControlService.exportItems(itemIds, findControlBehavior).pipe(
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy(),
      ).toPromise().then(result => {
        if (!result) {
          return;
        }

        if (result.Zip) {
          const filename = this.documentService.sanitizeImportExportFilename(
            `${findControlBehavior.Name}.zip`,
          );
          const blob = this.browserService.base64ToBlob(result.Zip, 'application/zip' );
          this.browserService.saveBlob(
            this.browserService.getDocument(),
            blob,
            filename,
          );
        } else if (result.Items) {
          for (const item of result.Items) {
            const filename = this.documentService.sanitizeFilename(
              `${item.DefaultFilename}.xml`,
            );
            const blob = new Blob([ item.Xml ], { type: 'application/xml' });
            this.browserService.saveBlob(
              this.browserService.getDocument(),
              blob,
              filename,
            );
          }
        }
      });

      return {
        completionPromise: promise,
      };
    });
  }

  onImportClick() {
    const findControlBehavior = this.findControlDto.FindControlBehavior;
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const promise = this.apFindControlImportModalService.openModal(findControlBehavior).pipe(
        mapToModalSuccessValue(),
        switchMap(file => {
          return this.apFileUploadService.convertToText(file);
        }),
        switchMap(importXmlText => {
          return this.apFindControlService.importItems(importXmlText, findControlBehavior);
        }),
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy(),
      ).toPromise().then(importedItem => {
        if (!importedItem) return;

        this.dataList.push(importedItem);
        if (this.radioButtonColumnFieldConfig) {
          this.radioButtonColumnFieldConfig.SelectedRowPk = importedItem.Pk;
          this.radioButtonColumnFieldConfig.OnSelectedCallback(importedItem);
        }
      });

      return {
        completionPromise: promise,
      };
    });
  }

  onReorderColumnsClick() {
    const findControlBehavior = this.findControlDto.FindControlBehavior;
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const promise = this.apFindControlReorderColumnsModalService.openModal(findControlBehavior).pipe(
        mapToModalSuccessValue(),
        switchMap(newConfig => {
          return this.apFindControlService.updateUserColumnsConfig(newConfig, findControlBehavior);
        }),
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy(),
      ).toPromise().then(newConfig => {
        if (newConfig) {
          this.onLoadConfigSuccess(newConfig, this.removeFiltersOption);
        }
      });

      return {
        completionPromise: promise,
      };
    });
  }

  onFiltersClick() {
    this.apUndoableOperationService.queueOperation(this.undoComponentId, () => {
      const promise = this.fcFilterModalService.openModal({
        findControlConfig: this.findControlDto,
        previousSearchParameters: this.userSearchParameters,
      }).pipe(
        reportErrorToUserAndServer(this.apNotificationService),
        this.takeUntilDestroy(),
      ).toPromise().then(modalResult => {
        if (modalResult?.isSuccess) {
          this.userSearchParameters = modalResult.resultValue;
          if (this.userSearchParameters.AdditionalSearchParameters.length === 0) {
            this.userSearchParameters = undefined;
          }
          this.loadData(this.formControl.value);
        }
      });

      return {
        completionPromise: promise,
      };
    });
  }

  onSearchClick() {
    this.removeFiltersOption = false;
    this.rowCount$.next(this.dataList.length);
    if (this.loadingData) {
      return;
    }
    this.formControl.setValue('', { emitEvent: false });
    this.loadData('%');
    this.displayCountLineItem();
  }

  private onLoadConfigComplete() {
    this.loadingConfig = false;
  }

  private onLoadDataComplete() {
    this.loadingData = false;
  }

  private onLoadConfigSuccess(data: FindControlInitDto, firstLoad = false) {
    this.findControlDto = data;
    this.maxRows = this.findControlDto.FindControlBehavior.MaxRows;
    this.displayFilterOption = this.prepopulateValue[this.findControlDto.FindControlBehavior.Prepopulate];
    this.clientColumnInfoList = this.apConfigurableTableService.mapFindConfigToTableConfig(
      this.findControlDto,
      this.mode !== FindControlMode.Administration, (dataDto: IColumnPk & ISelected) => this.onHyperLinkSelect(dataDto)
    );
    this.serverColumnItemList = this.MapConfigToVisibleConfig(this.findControlDto);

    this.appendAdditionalColumns();

    const searchString = firstLoad ? '' : this.formControl.value;
    this.loadData(searchString, firstLoad);

    this.finishLoad.emit(this.findControlDto);
  }

  private onLoadDataSuccess(resultDto: FindControlResultDto): void {
    this.dataList = resultDto.Rows;
    this.updateRowCount();
    this.finishLoadData.emit(resultDto);

    if (this.radioButtonColumnFieldConfig) {
      // Make sure that the radio button column isn't trying to select a row that doesn't exist.
      const preselectedPk = this.radioButtonColumnFieldConfig.SelectedRowPk;
      const selectedRowPk = preselectedPk && this.dataList.find(x => x.Pk === preselectedPk);
      if (!selectedRowPk) {
        const selectedRow = this.dataList.find(x => x.Selected);
        if (selectedRow) {
          this.radioButtonColumnFieldConfig.SelectedRowPk = selectedRow.Pk;
        } else {
          const item = this.dataList[0];
          if (item) {
            this.onRadioButtonChange(item);
          }

          this.radioButtonColumnFieldConfig.SelectedRowPk = item?.Pk;
        }
      }
    }
  }

  private onError() {
    // TODO implement
  }

  private onDeleteClick(item: IColumnPk & IDeleted) {
    this.deleteClick.emit(item);
  }

  private onModifyClick(item: IColumnPk & ISelected) {
    this.modifyClick.emit(item);
  }

  onNewClick() {
    this.newClick.emit();
  }

  private onRadioButtonChange(item: IColumnPk & ISelected) {
    item.Selected = true;
    this.onSelectChange(item);
  }

  private onSelectChange(item: IColumnPk & ISelected) {
    this.selectChange.emit(item);
  }

  private MapConfigToVisibleConfig(configDto: FindControlInitDto): Array<ColumnItemDto<string>> {
    const tableItemList: Array<ColumnItemDto<string>> = configDto.FindControlBehavior.ColumnsConfig.Columns.filter(
      (column) => column.Visible
    ).map((column) => {
      return { ColumnName: column.ColumnName };
    });
    return tableItemList;
  }

  private onHyperLinkSelect(dataDto: IColumnPk & ISelected) {
    this.hyperlinkClick.emit(dataDto);
  }

  private appendAdditionalColumns() {
    this.radioButtonColumnFieldConfig = undefined;
    if (this.mode === FindControlMode.MultiSelect) {
      this.addCheckboxColumn();
    } else {
      this.addRadioButtonColumn();
    }
    this.addAdditionalActionsColumn();
  }

  private addCheckboxColumn() {
    const checkboxColumnName = 'Selected';

    this.clientColumnInfoList.unshift({
      ColumnName: checkboxColumnName,
      FieldTypeObject: new ColumnCheckboxDto({
        OnChangeCallback: (dataItem) => this.onSelectChange(dataItem),
      }),
      HeaderDisplayName: '',
    });
    this.serverColumnItemList.unshift({ ColumnName: checkboxColumnName });
  }

  private addRadioButtonColumn() {
    const radioButtonColumnName = 'SingleSelected';
    this.radioButtonColumnFieldConfig = new ColumnRadioButtonDto<IColumnPk & ISelected>({
      GroupName: this.baseId + '-' + radioButtonColumnName,
      OnSelectedCallback: (item) => {
        this.onRadioButtonChange(item);
      },
    });

    this.clientColumnInfoList.unshift({
      ColumnName: radioButtonColumnName,
      FieldTypeObject: this.radioButtonColumnFieldConfig,
      HeaderDisplayName: '',
    });
    this.serverColumnItemList.unshift({ ColumnName: radioButtonColumnName });
  }

  private addAdditionalActionsColumn() {
    const actionColumnName = 'Action';
    const actionIcons: ActionIconDto[] = [];
    if (this.findControlDto.SystemPermission.IsModifyAllowed && !this.isModifyButtonHidden) {
      actionIcons.push({
        MatIcon: 'edit',
        OnActionCallback: (dataItem) => this.onModifyClick(dataItem),
      });
    }
    if (this.findControlDto.SystemPermission.IsDeleteAllowed && !this.isDeleteButtonHidden) {
      actionIcons.push({
        MatIcon: 'delete',
        OnActionCallback: (dataItem) => this.onDeleteClick(dataItem),
      });
    }
    if (actionIcons.length) {
      this.clientColumnInfoList.push({
        ColumnName: actionColumnName,
        FieldTypeObject: new ColumnActionIconsDto({
          ActionIcons: actionIcons,
        }),
        HeaderDisplayName: actionColumnName,
      });
      this.serverColumnItemList.push({ ColumnName: actionColumnName });
    }
  }

  private getAdditionalSearchParameters(checkbox: CheckboxData): void {
    if (this.getIsValueEqualToIsCheckboxShouldBeChecked(checkbox)) {
      if (checkbox.parameters) {
        if (this.additionalSearchParameters === null) {
          this.additionalSearchParameters = checkbox.parameters;
        } else {
          checkbox?.parameters?.AdditionalSearchParameters.forEach((parameter: any) => {
            this.additionalSearchParameters?.AdditionalSearchParameters?.push(parameter);
          });
        }
      }
    } else {
      this.additionalSearchParameters = this.catchAdditionalSearchParameters;
    }
  }

  private getIsValueEqualToIsCheckboxShouldBeChecked(checkbox: CheckboxData): boolean {
    return checkbox.formControl.value === checkbox.isCheckboxShouldBeChecked;
  }

  private getCustomData(): FindControlCustomData | undefined {
    if (this.customData) {
      this.customData.Items.Rows = this.customData.Items.Rows.map(item => {
        return { ...item, Selected: false };
      });
    }
    return this.customData;
  }
}
